import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { SERVICE_PACKAGES, packageById } from '../../shared/packages';
import {
  BUDGET_RANGES,
  COMPANY_SIZES,
  INDUSTRIES,
  ROLE_LEVELS,
  TIMELINES,
} from '../../shared/intake';

const ROLE_LABELS: Record<string, string> = {
  exec: 'Executive (C-level / VP / Founder)',
  director: 'Director',
  manager: 'Manager / Team lead',
  ic: 'Individual contributor',
  other: 'Other',
};
const SIZE_LABELS: Record<string, string> = {
  '1-10': '1-10', '11-50': '11-50', '51-200': '51-200', '201-1000': '201-1,000', '1000_plus': '1,000+',
};
const TIMELINE_LABELS: Record<string, string> = {
  asap: 'As soon as possible', this_quarter: 'This quarter', this_year: 'This year', exploring: 'Just exploring',
};
const BUDGET_LABELS: Record<string, string> = {
  under_5k: 'Under $5k', '5k_15k': '$5k - $15k', '15k_50k': '$15k - $50k', '50k_plus': '$50k+', unsure: 'Not sure yet',
};
const CLASSIFICATION_LABELS: Record<string, string> = {
  public: 'Public - nothing sensitive',
  internal: 'Internal - business data, not regulated',
  confidential: 'Confidential - customer or commercially sensitive data',
  restricted: 'Restricted - regulated / highly sensitive data',
};
const REGULATORY_LABELS: Record<string, string> = {
  none: 'Not regulated', some: 'Some obligations (privacy, sector rules)', regulated: 'Heavily regulated industry',
};

interface FieldErrors { [k: string]: string }

// Hoisted to module level so its identity is stable across renders — defining
// it inside Start() would remount the subtree (and drop input focus) on every
// keystroke.
function Field({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

export function Start() {
  const [params] = useSearchParams();
  const startedAt = useMemo(() => Date.now(), []);
  const utm = useMemo(() => {
    const u: Record<string, string> = {};
    for (const k of ['source', 'medium', 'campaign', 'term', 'content']) {
      const v = params.get(`utm_${k}`);
      if (v) u[k] = v;
    }
    return u;
  }, [params]);

  const [form, setForm] = useState({
    contact_name: '',
    contact_email: '',
    contact_role: '',
    role_level: 'manager',
    company_name: '',
    company_website: '',
    industry: 'Software & Technology',
    company_size: '11-50',
    primary_goal: '',
    offer_interest: params.get('package') && packageById(params.get('package')!) ? params.get('package')! : 'not_sure',
    ai_maturity_self_rating: 2,
    current_ai_tools: '',
    data_classification: 'internal',
    regulatory_exposure: 'none',
    timeline: 'this_quarter',
    budget_range: 'unsure',
    compelling_event: '',
    consent: false,
    website: '', // honeypot
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ recommended?: string } | null>(null);
  const [topError, setTopError] = useState('');

  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setTopError('');
    try {
      const res = await api.post<{ ok: boolean; recommended_offer?: string }>('/api/intake', {
        ...form,
        started_at: startedAt,
        utm: Object.keys(utm).length > 0 ? utm : undefined,
        referrer: document.referrer || undefined,
      });
      setDone({ recommended: res.recommended_offer });
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.body.fields) {
        setErrors(err.body.fields as FieldErrors);
        setTopError('Please fix the highlighted fields.');
      } else if (err instanceof ApiError && err.status === 429) {
        setTopError('Too many submissions from this network — please try again later.');
      } else {
        setTopError('Something went wrong submitting the form. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const pkg = packageById(done.recommended);
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center">
        <div className="card !p-8">
          <div className="mb-3 text-4xl">🧭</div>
          <h1 className="text-2xl font-bold text-slate-50">Inquiry received — thank you.</h1>
          <p className="mt-3 text-sm text-slate-400">
            Here's what happens next:
          </p>
          <ol className="mx-auto mt-4 max-w-md space-y-2 text-left text-sm text-slate-300">
            <li className="flex gap-2"><span className="font-bold text-brand-400">1.</span> Your inquiry is logged and an AI-prepared pre-consulting brief is drafted for our team.</li>
            <li className="flex gap-2"><span className="font-bold text-brand-400">2.</span> A consultant reviews it — usually same business day.</li>
            <li className="flex gap-2"><span className="font-bold text-brand-400">3.</span> We reach out to schedule a discovery call. No spam, no drip campaign.</li>
          </ol>
          {pkg && (
            <p className="mt-5 rounded-lg border border-brand-800/50 bg-brand-950/30 p-3 text-sm text-brand-200">
              Based on your answers, the likely starting point is <strong>{pkg.name}</strong> ({pkg.duration}).
              We'll confirm on the call.
            </p>
          )}
          <Link to="/" className="btn-secondary mt-6">← Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <Link to="/" className="text-xs text-slate-500 hover:text-slate-300">← VibeProSoft AI Consulting</Link>
      <h1 className="mt-2 text-3xl font-bold text-slate-50">Start your AI consulting inquiry</h1>
      <p className="mt-2 text-sm text-slate-400">
        Five minutes now saves a meeting later — your answers feed the maturity assessment and the
        pre-call brief, so the first conversation is already substantive.
      </p>

      {topError && <div className="mt-4 rounded-lg border border-red-900 bg-red-950/40 px-4 py-2 text-sm text-red-300">{topError}</div>}

      <form onSubmit={onSubmit} className="mt-6 space-y-8">
        <fieldset className="card space-y-4">
          <legend className="px-1 text-sm font-bold text-brand-300">About you</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field error={errors.contact_name} label="Your name">
              <input className="input" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} required />
            </Field>
            <Field error={errors.contact_email} label="Work email">
              <input type="email" className="input" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} required />
            </Field>
            <Field error={errors.contact_role} label="Your role / title">
              <input className="input" value={form.contact_role} onChange={(e) => set('contact_role', e.target.value)} placeholder="e.g. VP Engineering" />
            </Field>
            <Field error={errors.role_level} label="Seniority">
              <select className="input" value={form.role_level} onChange={(e) => set('role_level', e.target.value)}>
                {ROLE_LEVELS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </Field>
          </div>
          {/* Honeypot — humans never see this. */}
          <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
            <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set('website', e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset className="card space-y-4">
          <legend className="px-1 text-sm font-bold text-brand-300">Your company</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field error={errors.company_name} label="Company name">
              <input className="input" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} required />
            </Field>
            <Field error={errors.company_website} label="Website (optional)">
              <input className="input" value={form.company_website} onChange={(e) => set('company_website', e.target.value)} placeholder="acme.com" />
            </Field>
            <Field error={errors.industry} label="Industry">
              <select className="input" value={form.industry} onChange={(e) => set('industry', e.target.value)}>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field error={errors.company_size} label="Company size">
              <select className="input" value={form.company_size} onChange={(e) => set('company_size', e.target.value)}>
                {COMPANY_SIZES.map((s) => <option key={s} value={s}>{SIZE_LABELS[s]} people</option>)}
              </select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="card space-y-4">
          <legend className="px-1 text-sm font-bold text-brand-300">Your AI goal</legend>
          <Field error={errors.primary_goal} label="What do you want AI to do for your business?"
                 hint="The more specific, the better the pre-call brief — processes, pain points, what success looks like.">
            <textarea className="input min-h-28" value={form.primary_goal} onChange={(e) => set('primary_goal', e.target.value)} required minLength={20} maxLength={2000} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field error={errors.offer_interest} label="Which engagement interests you?">
              <select className="input" value={form.offer_interest} onChange={(e) => set('offer_interest', e.target.value)}>
                <option value="not_sure">Not sure — recommend one</option>
                {SERVICE_PACKAGES.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field error={errors.ai_maturity_self_rating} label="How mature is your AI practice today?">
              <select className="input" value={form.ai_maturity_self_rating} onChange={(e) => set('ai_maturity_self_rating', Number(e.target.value))}>
                <option value={1}>1 — Nothing formal yet</option>
                <option value={2}>2 — Individual experiments</option>
                <option value={3}>3 — Some team adoption</option>
                <option value={4}>4 — Established programs</option>
                <option value={5}>5 — AI is core to operations</option>
              </select>
            </Field>
          </div>
          <Field error={errors.current_ai_tools} label="AI tools in use today (optional)">
            <input className="input" value={form.current_ai_tools} onChange={(e) => set('current_ai_tools', e.target.value)} placeholder="e.g. ChatGPT Teams, Copilot, internal RAG bot" />
          </Field>
        </fieldset>

        <fieldset className="card space-y-4">
          <legend className="px-1 text-sm font-bold text-brand-300">Context</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field error={errors.data_classification} label="Most sensitive data involved"
                   hint="Drives which AI providers/architectures we'll recommend.">
              <select className="input" value={form.data_classification} onChange={(e) => set('data_classification', e.target.value)}>
                {Object.entries(CLASSIFICATION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field error={errors.regulatory_exposure} label="Regulatory exposure">
              <select className="input" value={form.regulatory_exposure} onChange={(e) => set('regulatory_exposure', e.target.value)}>
                {Object.entries(REGULATORY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field error={errors.timeline} label="When do you want to start?">
              <select className="input" value={form.timeline} onChange={(e) => set('timeline', e.target.value)}>
                {TIMELINES.map((t) => <option key={t} value={t}>{TIMELINE_LABELS[t]}</option>)}
              </select>
            </Field>
            <Field error={errors.budget_range} label="Budget range">
              <select className="input" value={form.budget_range} onChange={(e) => set('budget_range', e.target.value)}>
                {BUDGET_RANGES.map((b) => <option key={b} value={b}>{BUDGET_LABELS[b]}</option>)}
              </select>
            </Field>
          </div>
          <Field error={errors.compelling_event} label="Anything driving the timing? (optional)" hint="Board ask, audit, product launch, competitor move, budget cycle…">
            <input className="input" value={form.compelling_event} onChange={(e) => set('compelling_event', e.target.value)} />
          </Field>
        </fieldset>

        <div className="card">
          <label className="flex items-start gap-3 text-sm text-slate-300">
            <input type="checkbox" checked={form.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-teal-500" required />
            <span>
              I consent to VibeProSoft contacting me about this inquiry and processing the
              information above to prepare for it. We never sell or share your data.
            </span>
          </label>
          {errors.consent && <p className="mt-1 text-xs text-red-400">{errors.consent}</p>}
          <button type="submit" disabled={busy} className="btn-primary mt-4 w-full !py-3 !text-base">
            {busy ? 'Submitting…' : 'Submit inquiry'}
          </button>
          <p className="mt-2 text-center text-[11px] text-slate-600">
            Your submission creates a pre-consulting brief so our first call starts at depth, not at zero.
          </p>
        </div>
      </form>
    </div>
  );
}
